import React, { useCallback } from "react";
import { withFormik } from 'formik';
import * as Yup from "yup";

// core components
import { Input } from "stories/forms";
import { Button, Typography, Link } from "stories/components"
import { Card, CardBody, CardHeader, Col, Row } from "stories/layout"
import { useNotification } from "stories/components/Notification"
import { httpActivateUser } from "services/users";
import { httpSendActivationEmail } from "services/users"

import { logoutWhenTokenExp } from "services/logoutTokenExp";
import logo from "assets/images/logo_nav.png"

const ActivationForm = (props) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setErrors,
    setSubmitting,
    setValues,
    setTouched,
    validateForm,
  } = props;
  
  const {
    t,
    history,
    emailProps,
  } = props;
  
  const { notify } = useNotification();

  const getFormikInputProps = useCallback(
    name => ({
      name,
      value: values[name],
      error: Boolean(touched[name] && errors[name]),
      errorText: touched[name] ? errors[name] : "",
      onBlur: handleBlur,
      onChange: handleChange
    }),
    [values, errors, touched, handleBlur, handleChange]
  );

  const handleResendActivationEmail = (emailAdress) => {
    setSubmitting(true)
    httpSendActivationEmail(emailAdress).then(res => {
      notify({ title:t('Activation'), message:t('Pin-code sent successfully to your email')})
      history.push({
        pathname: '/auth/activation',
        state: { email: emailAdress}
      })
      setValues({
        ...values,
        pin_code: "",
      })
      setTouched({
        ...touched,
        pin_code: false,
      })
      setErrors({})
      setSubmitting(false)
    }).catch(err => {
      setSubmitting(false)
    })
  }

  const getForm = () => {
    return (
      <>
        <Row>
          <Col className="mb-3">
            <Input
              {...getFormikInputProps("pin_code")}
              placeholder={t('activation_pin_code','123456')}
              required
            />
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <Typography variant="small" className="mb-2 px-2">{t("activation_check_email_for_code",'Jos et saanut koodia tarkista roskaposti. ')} <Link onClick={() => handleResendActivationEmail(values?.email)} className="underline-text" >{t("activation_send_code_again",'Lähetä koodi uudelleen.')}</Link></Typography>
          </Col>
        </Row>
                              
        <Button disabled={isSubmitting} variant="secondary" onClick={() => validateForm().then(() => handleSubmit())}>{t("activation_confirm",'Vahvista')}</Button>
        <div className="text-start text-muted mb-4">
        </div>
        { Boolean(errors["general"]) && (
          <div className="text-center text-muted mb-2">
            <Typography className="text-danger">{errors["general"]}</Typography>
          </div>
        )}

        { errors?.['pin_code_expired_or_not_correct'] ? (
          <div className="text-start mb-4">
            <Button variant="secondary" disabled={isSubmitting} onClick={() => handleResendActivationEmail(values?.email)}>{t('login_resend_activation_email_button', 'Resend activation email')}</Button>
          </div>
        ) : null}
      </>
    )
  }
  
  if (emailProps) {
    return getForm();
  }
  
  return (
    <Card variant="secondary">
      <CardHeader className="bg-transparent pt-5 pb-1">
        <div className="text-center text-muted mb-4">
          <Typography variant="h2" className="mb-2 px-2">{t('activation_set_pin_code','Anna sähköpostiin lähetetty koodi ja vahvista tilisi')}</Typography>
          <Typography variant="p"></Typography>
        </div>
      </CardHeader>
      <CardBody className="px-lg-5 py-lg-5">
        <Row>
          <Col className="mb-1">
            <Input
              {...getFormikInputProps("pin_code")}
              placeholder={t('activation_pin_code','123456')}
              required
            />
          </Col>
        </Row>
                                  
        <div className="text-center text-muted mt-3 mb-4">
          <Button disabled={isSubmitting} onClick={() => validateForm().then(() => handleSubmit())}>{t('Vahvista')}</Button>
          { Boolean(errors["general"]) && <Typography className="text-danger">{errors["general"]}</Typography> }
        </div>
        { Boolean(errors["general"]) && (
          <div className="text-center text-muted mb-2">
            <Typography className="text-danger">{errors["general"]}</Typography>
          </div>
        )}

        { errors?.['pin_code_expired_or_not_correct'] ? (
          <div className="text-center mb-4">
            <Button variant="secondary" disabled={isSubmitting} onClick={() => handleResendActivationEmail(values?.email)}>{t('login_resend_activation_email_button', 'Resend activation email')}</Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

const defaultValues = {
  id: null,
}

const ActivationFormWithFormik = withFormik({
  validateOnMount: true,
  mapPropsToValues: props => {
    const { preSetValues } = props;
    if (preSetValues) {
      return preSetValues;
    } else {
      return defaultValues
    }
  },
  validationSchema: props => {
    const {t} = props;
    const required = t("required_field","Kenttä on pakollinen");
    
    return Yup.object().shape({
      pin_code: Yup.string().required(required).matches(/^[0-9]{6}$/, t("pin_code_must_be_6_digits","PIN-koodin tulee olla 6 numeroa")),
    });
  },
  handleSubmit: (values, { setSubmitting, setErrors, props }) => {
    const {t, emailProps, setActiveStep} = props;
    let data = {
      pin_code: values.pin_code,
      email: values.email,
    };
    
    httpActivateUser(data).then(res => {
      setSubmitting(false);
      if (res?.status === 200) {
        if (res?.data?.access && res?.data?.refresh){
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.setItem("refreshToken", res.data.refresh);
          localStorage.setItem("accessToken", res.data.access);

          logoutWhenTokenExp();
        }
        if(!emailProps){
          window.location.href = "/"
        }
        else{
          setActiveStep(4)
        }
      }
    }, error => {
      setSubmitting(false);
      
      const errorDataKey = Object.keys(error?.data);
      let errorCodeKey = errorDataKey?.[0];
      let errorCodeData = error?.data?.[errorCodeKey];
      
      if (errorDataKey.length >= 2 && errorDataKey.includes('errorCode')) {
        const errorCodeIndex = errorDataKey.indexOf('errorCode');
        errorCodeKey = errorDataKey[errorCodeIndex];
        errorCodeData = error?.data?.[errorCodeKey];
        errorDataKey.splice(errorDataKey.indexOf('errorCode'), 1);
        setErrors({[errorCodeData]: t(errorCodeData)});
      }
      
      let errorData = error?.data[errorDataKey?.[0]];
      const valuesKeys = Object.keys(values);
      const usualErrorkeys = ['detail', 'message', 'non_field_errors'];

      if (Array.isArray(errorData)) {
        errorData = errorData[0];
      }

      if (!errorData || errorData === '') {
        setErrors({general: t('general_error','Virhe. Ota tarvittaessa yhteyttä asiakaspalveluun'),[errorCodeData]: t(errorCodeData)})
      }
      else if (errorDataKey.length > 0 && valuesKeys.includes(errorDataKey?.[0])) {
        setErrors({[errorDataKey?.[0]]: t(errorCodeData,errorData),[errorCodeData]: t(errorCodeData)});
      }
      else if (errorDataKey.length > 0 && usualErrorkeys.includes(errorDataKey?.[0])) {
        setErrors({general: t(errorCodeData,errorData),[errorCodeData]: t(errorCodeData)});
      } 
      else if (errorDataKey.length > 0) {
        setErrors({general: t(errorCodeData,errorData),[errorCodeData]: t(errorCodeData)});
      }
      else {
        setErrors({
          general: `${t('general_error','Virhe. Ota tarvittaessa yhteys ylläpitoon.')} ${JSON.stringify(error)}`,[errorCodeData]: t(errorCodeData),
        })
      }
    })
  },
  displayName: "ActivationForm"
})(ActivationForm);

export default ActivationFormWithFormik; 